/****************************************************
 * Module Name:  kapanStatusSummaryPieChart.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useState } from "react";
import DashboardProxy from "../../utility/proxy/manager/dashboardProxy";
import Constants from "../../utility/constants";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import CommonSnackbar from "../common/commonSnackBar";
import KapanSummary from "../report/kapanSummary";
import Utility from "../../utility/utility";
import { FaBox } from "react-icons/fa";
import { SearchOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import Modal from "react-bootstrap/Modal";
import "./../../style/modalstyle.css";


let useColor = Utility.GetRandomColorForPieChart();

const KapanStatusSummaryPieChart = () => {
    const [chartData, setChartData] = useState([]);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [loader, setLoader] = useState(false);
    const [show, setShow] = useState(false);
    const [kapanid, setKapanId] = useState("");
    const [status, setStatus] = useState("");
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down("md"));


    const handleSearch = () => {
        let data = kapanid.split(/[ ,]+/).map(Number)
        setLoader(true);
        DashboardProxy.GetKapanStatusSummaryCount(
            data,
            (response) => {
                if (response) {
                    setChartData(response);
                    setLoader(false);
                } else {
                    setErrorSnackbar(true);
                    setLoader(false);
                }
            }
        );
    };


    function CustomTooltip({ payload, label, active }) {
        if (active) {
            return (
                <Box bgcolor={"#FFFFF7"} padding={2} sx={{ opacity: 0.8 }}>
                    <Typography>
                        Status: {payload[0].payload.status} <br />
                        Count: {payload[0].value}
                    </Typography>
                </Box>
            );
        }

        return null;
    }

    const handleModalOpen = (e) => {
        setStatus(e.payload.status);
        setShow(true);
    };

    return (
        <div>
            <Modal show={show} onHide={() => setShow(false)} dialogClassName="bhb" enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={Constants.COMPANY_LOGO}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;
                        Kapan Status Summary
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <KapanSummary report={true} kid={kapanid} sts={status} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={() => setShow(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            <Paper elevation={4}>
                <Card>
                    <CardHeader
                        avatar={<FaBox size={25} color={useColor[1 % useColor.length]} />}
                        title={
                            <Typography
                                component="span"
                                fontWeight={Constants.defaultFontWeight}
                                fontSize={"1.25rem"}
                            >
                                Kapan Status Summary
                            </Typography>
                        }
                        subheader="No. of Stone Status By kapan Id"
                    />
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                width: "43vw",
                                mb: 7,
                                mt: 2,
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: "10px",
                                justifyContent: "space-betweeen",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    width: "45%",
                                    gap: "20px",
                                }}
                            >
                                <Box sx={{ alignSelf: "center", mt: 1, width: "20%" }}>
                                    <Typography>kapan Id</Typography>
                                </Box>
                                <Box
                                    sx={{
                                        alignSelf: "center",
                                        width: "80%",
                                        mt: 1,
                                    }}
                                >
                                    <TextField
                                        id="outlined-multiline-static"
                                        label="Enter Comma seperated kapan Ids"
                                        placeholder="Comma seperated Kapan Ids"
                                        multiline
                                        fullWidth
                                        value={kapanid}
                                        onChange={(e) =>
                                            setKapanId(Utility.ConvertNewLineToComma(e.target.value))
                                        }
                                    />
                                </Box>
                            </Box>
                            <LoadingButton
                                loading={loader}
                                loadingPosition="start"
                                variant="contained"
                                onClick={() => handleSearch()}
                                startIcon={<SearchOutlined size="large" />}
                                sx={{ mt: 1 }}
                                disabled={
                                    kapanid.length === 0
                                }
                            >
                                Search
                            </LoadingButton>
                        </Box>
                        <Box height={matches ? 300 : 505} width={matches ? 320 : "45vw"}>
                            <ResponsiveContainer width="99%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        labelLine={true}
                                        label
                                        fill="#8884d8"
                                        dataKey="count"
                                        onClick={(e) => handleModalOpen(e)}
                                        nameKey="status"
                                        stroke="#000000"
                                        strokeWidth={1}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={useColor[index % useColor.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    </CardContent>
                </Card>
            </Paper>
        </div>
    );
};

export default KapanStatusSummaryPieChart;
